import { useSyncExternalStore } from 'react';

// =============================================================================
// useIsMobile — deteksi perangkat mobile (Android/iOS/phone) untuk memilih
// MobileLayout vs AppLayout. Strategi: UA string + UA Client Hints + viewport
// width + pointer:coarse. Override via ?ui=desktop|mobile atau localStorage.
// =============================================================================

const MOBILE_BREAKPOINT = 768;
const STORAGE_KEY = 'webgps-ui-mode';

export type UiMode = 'auto' | 'mobile' | 'desktop';

let cachedUaMobile: boolean | null = null;

function detectUaMobile(): boolean {
  if (cachedUaMobile !== null) return cachedUaMobile;
  if (typeof navigator === 'undefined') return false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const uaData = (navigator as any).userAgentData;
  if (uaData && typeof uaData.mobile === 'boolean') {
    cachedUaMobile = uaData.mobile;
    return uaData.mobile;
  }
  const ua = navigator.userAgent || '';
  const isMobileUa = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
  const isIPadOs =
    /Macintosh/.test(ua) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;
  cachedUaMobile = isMobileUa || isIPadOs;
  return cachedUaMobile;
}

function getOverride(): UiMode {
  if (typeof window === 'undefined') return 'auto';
  try {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('ui');
    if (flag === 'mobile' || flag === 'desktop') {
      try { window.localStorage.setItem(STORAGE_KEY, flag); } catch { /* ignore */ }
      return flag;
    }
  } catch { /* ignore */ }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'mobile' || stored === 'desktop') return stored;
  } catch { /* ignore */ }
  return 'auto';
}

export function setUiMode(mode: UiMode): void {
  if (typeof window === 'undefined') return;
  try {
    if (mode === 'auto') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, mode);
  } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('webgps:ui-mode-change'));
}

interface UiModeSnapshot {
  isMobile: boolean;
  override: UiMode;
}

function readSnapshot(): UiModeSnapshot {
  const override = getOverride();
  if (override === 'mobile') return { isMobile: true, override };
  if (override === 'desktop') return { isMobile: false, override };
  const uaMobile = detectUaMobile();
  const width = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const coarsePointer =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
  const isMobile = uaMobile || (width < MOBILE_BREAKPOINT && coarsePointer);
  return { isMobile, override };
}

let currentSnapshot: UiModeSnapshot = readSnapshot();

function refreshSnapshot(): void {
  const next = readSnapshot();
  if (next.isMobile !== currentSnapshot.isMobile || next.override !== currentSnapshot.override) {
    currentSnapshot = next;
  }
}

function subscribe(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const onChange = () => { refreshSnapshot(); cb(); };
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  window.addEventListener('storage', onChange);
  window.addEventListener('webgps:ui-mode-change', onChange);
  const mq = window.matchMedia?.('(pointer: coarse)');
  mq?.addEventListener?.('change', onChange);
  return () => {
    window.removeEventListener('resize', onChange);
    window.removeEventListener('orientationchange', onChange);
    window.removeEventListener('storage', onChange);
    window.removeEventListener('webgps:ui-mode-change', onChange);
    mq?.removeEventListener?.('change', onChange);
  };
}

function getSnapshot(): UiModeSnapshot { return currentSnapshot; }
function getServerSnapshot(): UiModeSnapshot { return { isMobile: false, override: 'auto' }; }

export function useIsMobile(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot).isMobile;
}

export function useUiMode(): { isMobile: boolean; override: UiMode; setMode: (m: UiMode) => void } {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return { ...snap, setMode: setUiMode };
}

if (typeof window !== 'undefined') {
  setTimeout(refreshSnapshot, 0);
}
